import moment from 'moment'
import React, { FC } from 'react'
import Select from 'react-select'
import { KTIcon } from 'src/marketConnect/helpers'
import AppDatePickerRange from '../../orders/components/AppDatePickerRange'
import {
  FilterOption,
  filterSelectStyles,
  multiFilterSelectStyles,
  statusFilterOptions,
} from '../utils/customersFilterHelpers'
import '../manage-customers-filters.scss'

type CustomersFilterBarProps = {
  isUserStaff: boolean
  searchString: string
  onSearchChange: (value: string) => void
  onSearchSubmit: () => void
  selectedPlatforms: FilterOption[]
  platformOptions: FilterOption[]
  onPlatformsChange: (options: FilterOption[]) => void
  billingPlanOptions: FilterOption[]
  selectedBillingPlanId: string
  onBillingPlanChange: (value: string) => void
  selectedStatus: string
  onStatusChange: (value: string) => void
  selectedSource: string
  sourceOptions: FilterOption[]
  onSourceChange: (value: string) => void
  syncFromDate: string
  syncToDate: string
  showDateRangePickerForOrders: boolean
  onToggleOrdersDate: () => void
  onCloseOrdersDate: () => void
  onPickOrdersDateRange: (startDate: Date | string, endDate: Date | string) => void
  fromDate: string
  toDate: string
  showDateRangePicker: boolean
  onToggleCustomerDate: () => void
  onCloseCustomerDate: () => void
  onPickCustomerDateRange: (startDate: Date | string, endDate: Date | string) => void
  onResetFilters: () => void
  canSendCustomerEmail?: boolean
  canDeleteAccess?: boolean
  selectedCustomerCount?: number
  onSendEmail?: () => void
  onBulkDelete?: () => void
}

const CustomersFilterBar: FC<CustomersFilterBarProps> = ({
  isUserStaff,
  searchString,
  onSearchChange,
  onSearchSubmit,
  selectedPlatforms,
  platformOptions,
  onPlatformsChange,
  billingPlanOptions,
  selectedBillingPlanId,
  onBillingPlanChange,
  selectedStatus,
  onStatusChange,
  selectedSource,
  sourceOptions,
  onSourceChange,
  syncFromDate,
  syncToDate,
  showDateRangePickerForOrders,
  onToggleOrdersDate,
  onCloseOrdersDate,
  onPickOrdersDateRange,
  fromDate,
  toDate,
  showDateRangePicker,
  onToggleCustomerDate,
  onCloseCustomerDate,
  onPickCustomerDateRange,
  onResetFilters,
  canSendCustomerEmail = false,
  canDeleteAccess = false,
  selectedCustomerCount = 0,
  onSendEmail,
  onBulkDelete,
}) => {
  const hasBulkSelection = selectedCustomerCount > 0
  const sourceSelectOptions = sourceOptions || []
  const selectedSourceOption =
    sourceSelectOptions.find((option) => option.value === selectedSource) ??
    (selectedSource ? {value: selectedSource, label: selectedSource} : null)

  return (
    <div className='card-header bg-white px-4 py-4'>
      <div className='d-flex flex-column gap-3 w-100 min-w-0'>
        <div className='row g-2 align-items-center'>
          <div className={isUserStaff ? 'col-12' : 'col-12 col-md-4 col-lg-4 col-xl-3'}>
            <input
              className='form-control input-sm'
              type='search'
              placeholder='Search By Name or Email'
              aria-label='Search'
              onChange={(ev) => onSearchChange(ev.target.value)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  onSearchSubmit()
                }
              }}
              value={searchString}
              style={{ height: '38px', padding: '0.5rem 0.75rem' }}
            />
          </div>

          {!isUserStaff && (
            <div className='col-12 col-md-8 col-lg-8 col-xl-9'>
              <Select
                isMulti
                options={platformOptions}
                value={selectedPlatforms}
                onChange={(selectedOptions) => onPlatformsChange([...(selectedOptions || [])])}
                placeholder='All Platforms'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={multiFilterSelectStyles}
              />
            </div>
          )}
        </div>

        <div className='d-flex flex-wrap align-items-center gap-2 manage-customers-filter-row'>
          {!isUserStaff && (
            <>
              <div className='manage-customers-filter-field manage-customers-filter-field--select'>
                <Select
                  isClearable
                  options={billingPlanOptions}
                  value={
                    billingPlanOptions.find((option) => option.value === selectedBillingPlanId) || null
                  }
                  onChange={(selectedOption) => onBillingPlanChange(selectedOption?.value || '')}
                  placeholder='All Billing Plans'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={filterSelectStyles}
                />
              </div>

              <div className='manage-customers-filter-field manage-customers-filter-field--select'>
                <Select
                  isClearable
                  options={statusFilterOptions}
                  value={statusFilterOptions.find((option) => option.value === selectedStatus) || null}
                  onChange={(selectedOption) => onStatusChange(selectedOption?.value || '')}
                  placeholder='All Statuses'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={filterSelectStyles}
                />
              </div>

              <div className='manage-customers-filter-field manage-customers-filter-field--select'>
                <Select
                  isClearable
                  options={sourceSelectOptions}
                  value={selectedSourceOption}
                  onChange={(selectedOption) => onSourceChange(selectedOption?.value || '')}
                  placeholder='All Sources'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  menuPosition='fixed'
                  styles={filterSelectStyles}
                />
              </div>

              <div className='manage-customers-filter-date manage-customers-filter-date--orders'>
                <button
                  type='button'
                  className='btn btn-light border rounded w-100 d-flex align-items-center justify-content-center text-truncate fs-8 px-2'
                  style={{ height: '38px' }}
                  title={
                    syncFromDate && syncToDate
                      ? `${moment(syncFromDate).format('YYYY/MM/DD')} - ${moment(syncToDate).format('YYYY/MM/DD')}`
                      : 'Filter Orders'
                  }
                  onClick={onToggleOrdersDate}
                >
                  <KTIcon iconName='calendar' className='fs-6 me-1 flex-shrink-0' />
                  <span className='text-truncate'>
                    {syncFromDate && syncToDate
                      ? `${moment(syncFromDate).format('MM/DD/YY')} - ${moment(syncToDate).format('MM/DD/YY')}`
                      : 'Filter Orders'}
                  </span>
                </button>
                <AppDatePickerRange
                  isVisible={showDateRangePickerForOrders}
                  handleClose={onCloseOrdersDate}
                  onPickRange={({ startDate, endDate }) => onPickOrdersDateRange(startDate, endDate)}
                />
              </div>

              <div className='manage-customers-filter-date manage-customers-filter-date--customer'>
                <button
                  type='button'
                  className='btn btn-light border rounded w-100 d-flex align-items-center justify-content-center text-truncate fs-8 px-2'
                  style={{ height: '38px' }}
                  title={
                    fromDate && toDate
                      ? `${moment(fromDate).format('YYYY/MM/DD')} - ${moment(toDate).format('YYYY/MM/DD')}`
                      : 'Filter Customer By Date'
                  }
                  onClick={onToggleCustomerDate}
                >
                  <KTIcon iconName='calendar' className='fs-6 me-1 flex-shrink-0' />
                  <span className='text-truncate'>
                    {fromDate && toDate
                      ? `${moment(fromDate).format('MM/DD/YY')} - ${moment(toDate).format('MM/DD/YY')}`
                      : 'Customer Date'}
                  </span>
                </button>
                <AppDatePickerRange
                  isVisible={showDateRangePicker}
                  handleClose={onCloseCustomerDate}
                  onPickRange={({ startDate, endDate }) => onPickCustomerDateRange(startDate, endDate)}
                />
              </div>
            </>
          )}

          <div
            className={`manage-customers-filter-actions d-flex flex-wrap gap-2 ${
              isUserStaff ? '' : 'ms-lg-auto'
            }`}
          >
            <button
              type='button'
              className='btn fs-8 btn-sm btn-primary fw-bold text-nowrap'
              onClick={onResetFilters}
            >
              Reset Filters
            </button>

            {hasBulkSelection && canSendCustomerEmail ? (
              <button
                type='button'
                className='btn fs-8 btn-sm btn-light-primary fw-bold text-nowrap'
                onClick={onSendEmail}
              >
                <KTIcon iconName='sms' className='fs-5 me-1' />
                Send email
              </button>
            ) : null}

            {hasBulkSelection && canDeleteAccess ? (
              <button
                type='button'
                className='btn fs-8 btn-sm btn-danger fw-bold text-nowrap'
                onClick={onBulkDelete}
              >
                <KTIcon iconName='trash' className='fs-5 me-1' />
                Delete selected
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CustomersFilterBar
